import { GetStaticProps } from 'next';
import React from 'react';

import { NewsOverviewSingleFieldsFragment, PageInformationFieldsFragment } from 'api/graphql-types';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetNewsOverview } from 'api/graphql-helper';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import IntroHeader from 'modules/intro-header/intro-header';

import { initialState as settingsInitialState } from 'redux/stores/settings';
import { initialState as newsInitialState } from 'redux/stores/news';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { ROUTES } from 'constants/routes';
import { IBasePageProps } from 'types/page';
import { searchNewsArticles } from 'api/algolia';
import { NEWS_ARTICLES_OVERVIEW_LIMIT } from 'modules/news-overview/data';

interface INewsOverviewPageProps extends IBasePageProps {
	newsOverview?: NewsOverviewSingleFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
}

const NewsOverviewPage = (props: INewsOverviewPageProps): React.JSX.Element => {
	const modules = props.newsOverview?.modules || [];
	const seoModule = props?.newsOverview?.seo;

	return (
		<Layout navbar={props?.pageInformation?.navbar} footer={props?.pageInformation?.footer}>
			<Head seoModule={seoModule} seoParent={props.pageInformation?.seo} />
			<IntroHeader module={props?.newsOverview?.header} slug={ROUTES.NEWS.fullPath} />
			<ModuleBuilder modules={modules} slug={ROUTES.NEWS.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetNewsOverview({ variables: { locale, publicationState } }, client);
		const { pageInformation, newsOverview, themes_connection: themes } = result.props.data;

		const { results: newsArticles, total: newsArticlesCount } = await searchNewsArticles(
			'',
			{
				themesIds: []
			},
			{
				limit: NEWS_ARTICLES_OVERVIEW_LIMIT,
				page: 1,
				sort: 'desc',
				locale
			},
			'gelderland'
		);

		if (newsOverview == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				newsOverview: newsOverview.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'news-overview',
						contentTypeId: newsOverview?.data?.documentId
					},
					news: {
						...newsInitialState,
						newsArticles,
						newsArticlesCount
					},
					themes: {
						themes: themes?.data || [],
						totalThemes: themes?.pageInfo.total || 0
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in news overview', error);

		return {
			notFound: true
		};
	}
};

export default NewsOverviewPage;
